import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config";
import FilterButton from "../components/FilterButton";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart3,
  DollarSign,
  Target,
  Users,
  TrendingUp,
  TrendingDown,
  Award,
  Zap,
  Activity,
  Percent,
  ArrowUpRight,
  Sparkles,
  Crown,
  Medal,
  Trophy,
} from "lucide-react";

/* ===================== Helpers ===================== */
const getNYDateTime = (options = {}) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    ...options,
  });
  return Object.fromEntries(
    formatter.formatToParts(new Date()).map((p) => [p.type, p.value])
  );
};

const getNYMonthStart = () => {
  const now = getNYDateTime({ year: "numeric", month: "2-digit" });
  return `${now.year}-${now.month}-01T00:00:00`;
};

const getNYNow = () => {
  const now = getNYDateTime({
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return `${now.year}-${now.month}-${now.day}T${now.hour}:${now.minute}:${now.second}`;
};

const addHoursUTC = (isoString, hours) => {
  const date = new Date(isoString.endsWith("Z") ? isoString : isoString + "Z");
  date.setUTCHours(date.getUTCHours() + hours);
  return date.toISOString();
};

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "0";
  const n = Number(v);
  if (Number.isNaN(n)) return "0";
  return n >= 1000
    ? n.toLocaleString()
    : n % 1 === 0
    ? n.toFixed(0)
    : n.toFixed(2);
};

/* ===================== Dashboard Component ===================== */
const Dashboard = () => {
  const [dateRange, setDateRange] = useState({
    startDate: getNYMonthStart(),
    endDate: getNYNow(),
  });
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const startDateAdjusted = addHoursUTC(dateRange.startDate, 5);
        const endDateAdjusted = addHoursUTC(dateRange.endDate, 5);
        const res = await fetch(
          `${BASE_URL}analyticsdashboard.php?startDate=${encodeURIComponent(
            startDateAdjusted
          )}&endDate=${encodeURIComponent(endDateAdjusted)}`,
          { credentials: "include" }
        );
        const json = await res.json();
        if (json?.status === "success") setDashboard(json);
        else {
          console.error("dashboard API error:", json);
          setDashboard(null);
        }
      } catch (err) {
        console.error("fetch failed:", err);
        setDashboard(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [dateRange]);

  const kpis = dashboard?.kpis || {};
  const expenses = dashboard?.expenses || {};
  const topAgentsMco = dashboard?.top_agents_mco || [];
  const topAgentsConv = dashboard?.top_agents_conversion || [];
  const topAgentsRevRatio = dashboard?.top_agents_revenue_ratio || [];
  const topAgentsGm = dashboard?.top_agents_gross_margin || [];
  const topCampMco = dashboard?.top_campaigns_mco || [];
  const topCampConv = dashboard?.top_campaigns_conversion || [];
  const topCampRev = dashboard?.top_campaigns_revenue_ratio || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white relative overflow-hidden">
      {/* Animated Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-500" />
      </div>

      <main className="relative z-10 max-w-[1600px] mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
        >
          <div className="flex items-center gap-4">
            <motion.div
              whileHover={{ rotate: 360, scale: 1.1 }}
              transition={{ duration: 0.6 }}
              className="relative p-4 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 backdrop-blur-xl border border-cyan-400/30 shadow-2xl"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/20 to-transparent rounded-2xl" />
              <BarChart3 size={32} className="text-cyan-300 relative z-10" />
            </motion.div>
            <div>
              <h1 className="text-4xl font-black bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 animate-gradient">
                Analytics Dashboard
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                Real-time marketing intelligence & performance metrics
              </p>
            </div>
          </div>

          <FilterButton
            dateRange={dateRange}
            onApply={(r) =>
              setDateRange({ startDate: r.startDate, endDate: r.endDate })
            }
          />
        </motion.div>

        {/* KPIs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-orange-400">
              Key Performance Indicators
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-40 rounded-2xl bg-slate-800/50 animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <KpiCard
                label="Total MCO"
                value={`$${fmt(kpis.totalMco)}`}
                icon={<DollarSign />}
                gradient="from-emerald-500 to-teal-600"
                glowColor="emerald"
                delay={0}
              />
              <KpiCard
                label="Marketing Spend"
                value={`$${fmt(kpis.totalMarketing)}`}
                icon={<TrendingUp />}
                gradient="from-blue-500 to-indigo-600"
                glowColor="blue"
                delay={0.1}
              />
              <KpiCard
                label="Conversion Rate"
                value={`${fmt(kpis.conversionRatio)}%`}
                icon={<Target />}
                gradient="from-purple-500 to-pink-600"
                glowColor="purple"
                delay={0.2}
              />
              <KpiCard
                label="Revenue Ratio"
                value={fmt(kpis.revenueRatio)}
                icon={<Zap />}
                gradient="from-orange-500 to-red-600"
                glowColor="orange"
                delay={0.3}
              />
            </div>
          )}
        </motion.div>

        {/* Top Performers Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400">
              Top Performers
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Agents Column */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-5 h-5 text-cyan-400" />
                <h3 className="text-xl font-bold text-cyan-300">
                  Top Agents
                </h3>
              </div>

              <PremiumList
                title="Highest MCO"
                data={topAgentsMco}
                label="mco"
                isMoney
                icon={<DollarSign className="w-4 h-4" />}
                gradient="from-emerald-500/20 to-teal-500/20"
                accentColor="emerald"
              />
              <PremiumList
                title="Best Gross Margin"
                data={topAgentsGm}
                label="gross_margin"
                isMoney
                icon={<Activity className="w-4 h-4" />}
                gradient="from-blue-500/20 to-cyan-500/20"
                accentColor="blue"
              />
              <PremiumList
                title="Top Conversion Rate"
                data={topAgentsConv}
                label="conversion"
                suffix="%"
                icon={<Target className="w-4 h-4" />}
                gradient="from-purple-500/20 to-pink-500/20"
                accentColor="purple"
              />
              <PremiumList
                title="Best Revenue Ratio"
                data={topAgentsRevRatio}
                label="rev_ratio"
                icon={<TrendingUp className="w-4 h-4" />}
                gradient="from-orange-500/20 to-red-500/20"
                accentColor="orange"
              />
            </motion.div>

            {/* Campaigns Column */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-5 h-5 text-yellow-400" />
                <h3 className="text-xl font-bold text-yellow-300">
                  Top Campaigns
                </h3>
              </div>

              <PremiumList
                title="Highest MCO"
                data={topCampMco}
                label="mco"
                isMoney
                isCampaign
                icon={<DollarSign className="w-4 h-4" />}
                gradient="from-emerald-500/20 to-teal-500/20"
                accentColor="emerald"
              />
              <PremiumList
                title="Top Conversion Rate"
                data={topCampConv}
                label="conversion"
                suffix="%"
                isCampaign
                icon={<Percent className="w-4 h-4" />}
                gradient="from-purple-500/20 to-pink-500/20"
                accentColor="purple"
              />
              <PremiumList
                title="Best Revenue Ratio"
                data={topCampRev}
                label="rev_ratio"
                isCampaign
                icon={<ArrowUpRight className="w-4 h-4" />}
                gradient="from-orange-500/20 to-red-500/20"
                accentColor="orange"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Expenses Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <Activity className="w-5 h-5 text-red-400" />
            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-orange-400">
              Expense Breakdown
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <ExpenseCard
              title="Tools Cost"
              value={expenses.tool ?? expenses.toolCost}
              icon={<Activity className="w-6 h-6" />}
              gradient="from-red-500/20 to-orange-500/20"
              accentColor="red"
              delay={0}
            />
            <ExpenseCard
              title="Revenue Gen Cost"
              value={expenses.rev ?? expenses.revGenCost}
              icon={<TrendingDown className="w-6 h-6" />}
              gradient="from-orange-500/20 to-yellow-500/20"
              accentColor="orange"
              delay={0.1}
            />
            <ExpenseCard
              title="Admin Cost"
              value={expenses.admin ?? expenses.adminCost}
              icon={<Users className="w-6 h-6" />}
              gradient="from-pink-500/20 to-rose-500/20"
              accentColor="pink"
              delay={0.2}
            />
          </div>
        </motion.div>
      </main>

      {/* Custom styles for gradient animation */}
      <style jsx>{`
        @keyframes gradient {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

/* ===================== Subcomponents ===================== */

const KpiCard = ({ label, value, icon, gradient, glowColor, delay }) => {
  const glowColors = {
    emerald: "shadow-emerald-500/50",
    blue: "shadow-blue-500/50",
    purple: "shadow-purple-500/50",
    orange: "shadow-orange-500/50",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative"
    >
      {/* Glow effect */}
      <div
        className={`absolute -inset-1 bg-gradient-to-r ${gradient} rounded-2xl blur-xl opacity-25 group-hover:opacity-75 transition duration-500`}
      />

      {/* Card content */}
      <div className="relative rounded-2xl bg-slate-900/90 backdrop-blur-xl border border-white/10 p-6 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
          <div
            className={`w-full h-full bg-gradient-to-br ${gradient} rounded-full blur-2xl`}
          />
        </div>

        {/* Icon */}
        <div
          className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${gradient} mb-4 text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}
        >
          {icon}
        </div>

        {/* Label */}
        <div className="text-sm font-medium text-slate-400 mb-2">{label}</div>

        {/* Value */}
        <div className="text-3xl font-black text-white tracking-tight">
          {value}
        </div>

        {/* Trend indicator */}
        <div className="mt-3 flex items-center gap-2 text-xs text-emerald-400">
          <TrendingUp className="w-3 h-3" />
          <span>Active</span>
        </div>
      </div>
    </motion.div>
  );
};

const PremiumList = ({
  title,
  data,
  label,
  suffix = "",
  isMoney = false,
  isCampaign = false,
  icon,
  gradient,
  accentColor,
}) => {
  const medalColors = [
    {
      bg: "from-yellow-500 to-yellow-600",
      icon: <Crown className="w-4 h-4" />,
      glow: "shadow-yellow-500/50",
    },
    {
      bg: "from-slate-400 to-slate-500",
      icon: <Medal className="w-4 h-4" />,
      glow: "shadow-slate-500/50",
    },
    {
      bg: "from-orange-600 to-orange-700",
      icon: <Award className="w-4 h-4" />,
      glow: "shadow-orange-500/50",
    },
  ];

  const accentColors = {
    emerald: "border-emerald-500/30 text-emerald-400",
    blue: "border-blue-500/30 text-blue-400",
    purple: "border-purple-500/30 text-purple-400",
    orange: "border-orange-500/30 text-orange-400",
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      whileHover={{ scale: 1.02 }}
      className="group relative"
    >
      {/* Glow effect */}
      <div
        className={`absolute -inset-0.5 bg-gradient-to-r ${gradient} rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition duration-300`}
      />

      {/* Card */}
      <div className="relative rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className={`px-5 py-4 border-b ${accentColors[accentColor]}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {icon}
              <h4 className="font-bold text-sm">{title}</h4>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-white/5">
              Top 3
            </span>
          </div>
        </div>

        {/* List */}
        <div className="p-4 space-y-2">
          {!Array.isArray(data) || data.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Trophy className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No data available</p>
            </div>
          ) : (
            data.map((it, i) => {
              const key =
                label in it
                  ? label
                  : label === "gross_margin" && it.gross_margin !== undefined
                  ? "gross_margin"
                  : Object.keys(it).find(
                      (k) => k !== (isCampaign ? "campaign" : "agent")
                    );
              const raw = it[label] ?? it[key] ?? 0;
              const shown = isMoney
                ? `$${fmt(raw)}`
                : suffix
                ? `${raw}${suffix}`
                : fmt(raw);

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`flex items-center justify-between p-3 rounded-xl ${
                    i === 0
                      ? "bg-gradient-to-r from-yellow-500/10 to-transparent border border-yellow-500/20"
                      : "bg-white/5 hover:bg-white/10"
                  } transition-all duration-200 group/item`}
                >
                  <div className="flex items-center gap-3">
                    {/* Medal */}
                    <div
                      className={`relative w-10 h-10 rounded-xl bg-gradient-to-br ${medalColors[i].bg} flex items-center justify-center ${medalColors[i].glow} shadow-lg`}
                    >
                      {medalColors[i].icon}
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping" />
                    </div>

                    {/* Name */}
                    <div>
                      <div className="font-semibold text-white text-sm group-hover/item:text-cyan-300 transition-colors">
                        {isCampaign ? it.campaign ?? "-" : it.agent ?? "-"}
                      </div>
                      <div className="text-xs text-slate-500">
                        Rank #{i + 1}
                      </div>
                    </div>
                  </div>

                  {/* Value */}
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-black text-white">
                      {shown}
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-emerald-400 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </motion.div>
  );
};

const ExpenseCard = ({ title, value, icon, gradient, accentColor, delay }) => {
  const accentColors = {
    red: "border-red-500/30 text-red-400",
    orange: "border-orange-500/30 text-orange-400",
    pink: "border-pink-500/30 text-pink-400",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      whileHover={{ y: -5 }}
      className="group relative"
    >
      {/* Glow */}
      <div
        className={`absolute -inset-0.5 bg-gradient-to-r ${gradient} rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition duration-300`}
      />

      {/* Card */}
      <div className="relative rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-white/10 p-6 text-center overflow-hidden">
        {/* Background icon */}
        <div className="absolute top-0 right-0 opacity-5 transform translate-x-4 -translate-y-4">
          <div className="text-[120px]">{icon}</div>
        </div>

        {/* Icon */}
        <div
          className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${gradient} ${accentColors[accentColor]} mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}
        >
          {icon}
        </div>

        {/* Title */}
        <div className="text-sm font-medium text-slate-400 mb-3">{title}</div>

        {/* Value */}
        <div className={`text-3xl font-black ${accentColors[accentColor]}`}>
          ${fmt(value ?? 0)}
        </div>

        {/* Status */}
        <div className="mt-4 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/5 text-xs text-slate-400">
          <Activity className="w-3 h-3" />
          <span>Active</span>
        </div>
      </div>
    </motion.div>
  );
};

export default Dashboard;